import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import {
  listarPrestadoresPj,
  obterSaldosFundosPj,
  registrarSaqueFundoPj,
} from '~/Services/Service';

const nomesFundos = {
  3: '13ª NF',
  4: '14ª NF',
  F: 'Fundo Indenizatório',
};

const fundosOptions = [
  { value: '3', label: '13ª NF' },
  { value: '4', label: '14ª NF' },
  { value: 'F', label: 'Fundo Indenizatório' },
];

const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const normalizarDecimal = value => {
  const texto = String(value || '').trim().replace(',', '.');
  return texto === '' ? NaN : Number(texto);
};

const schema = saldoAtual => Yup.object().shape({
  tipoFundo: Yup.string()
    .required('Fundo obrigatório')
    .oneOf(['3', '4', 'F'], 'Fundo inválido'),
  codigoPj: Yup.number()
    .required('Prestador obrigatório')
    .positive('Prestador obrigatório'),
  valor: Yup.number()
    .transform((_, originalValue) => normalizarDecimal(originalValue))
    .typeError('Valor inválido')
    .required('Valor obrigatório')
    .positive('Valor deve ser maior que zero')
    .max(Number(saldoAtual || 0), `Saldo insuficiente. Saldo atual: ${moeda(saldoAtual)}`),
  observacao: Yup.string().max(500, 'Observação deve conter até 500 caracteres'),
});

const ModalSaque = ({
  show,
  onHide,
  onSalvo,
}) => {
  const [prestadoresComSaldo, setPrestadoresComSaldo] = useState([]);
  const [carregandoPrestadores, setCarregandoPrestadores] = useState(false);
  const [saldoAtual, setSaldoAtual] = useState(0);

  const formik = useFormik({
    initialValues: {
      tipoFundo: '',
      codigoPj: '',
      valor: '',
      observacao: '',
    },
    validationSchema: schema(saldoAtual),
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        await registrarSaqueFundoPj({
          codigo_pj: Number(values.codigoPj),
          tipo_fundo: values.tipoFundo,
          valor: normalizarDecimal(values.valor),
          observacao: values.observacao,
        });
        toast.success('Saque registrado.');
        if (onSalvo) await onSalvo();
      } catch (err) {
        toast.error(err.message || 'Erro ao registrar saque.');
      }
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setPrestadoresComSaldo([]);
      setSaldoAtual(0);
      setCarregandoPrestadores(false);
      return;
    }

    if (!formik.values.tipoFundo) {
      setPrestadoresComSaldo([]);
      setSaldoAtual(0);
      return;
    }

    let ativo = true;
    setCarregandoPrestadores(true);
    listarPrestadoresPj()
      .then(async resultado => {
        const prestadores = Array.isArray(resultado) ? resultado : [];
        const saldosPorPrestador = await Promise.all(
          prestadores.map(async prestador => {
            try {
              const saldos = await obterSaldosFundosPj(prestador.codigo);
              const saldoFundo = (Array.isArray(saldos) ? saldos : [])
                .find(item => item.tipo_fundo === formik.values.tipoFundo);

              return {
                prestador,
                saldo: saldoFundo ? Number(saldoFundo.saldo || 0) : 0,
              };
            } catch (_) {
              return { prestador, saldo: 0 };
            }
          })
        );

        if (!ativo) return;
        setPrestadoresComSaldo(
          saldosPorPrestador
            .filter(item => item.saldo > 0)
            .map(item => ({
              value: item.prestador.codigo,
              label: item.prestador.nome_fantasia || item.prestador.razao_social,
              saldo: item.saldo,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(err => toast.error(err.message || 'Erro ao carregar prestadores com saldo.'))
      .finally(() => {
        if (ativo) setCarregandoPrestadores(false);
      });

    return () => {
      ativo = false;
    };
  }, [show, formik.values.tipoFundo]);

  const prestadorSelecionado = useMemo(() => (
    prestadoresComSaldo.find(option => option.value === Number(formik.values.codigoPj)) || null
  ), [prestadoresComSaldo, formik.values.codigoPj]);

  const fundoSelecionado = useMemo(() => (
    fundosOptions.find(option => option.value === formik.values.tipoFundo) || null
  ), [formik.values.tipoFundo]);

  const alterarFundo = option => {
    formik.setFieldValue('tipoFundo', option ? option.value : '');
    formik.setFieldValue('codigoPj', '');
    formik.setFieldValue('valor', '');
    setPrestadoresComSaldo([]);
    setSaldoAtual(0);
  };

  const alterarPrestador = option => {
    formik.setFieldValue('codigoPj', option ? option.value : '');
    formik.setFieldValue('valor', '');
    setSaldoAtual(option ? option.saldo : 0);
  };

  const erroCampo = name => (
    formik.touched[name] && formik.errors[name] ? (
      <div className="hpj-field-error">{formik.errors[name]}</div>
    ) : null
  );

  return (
    <Modal
      className="hpj-modal hpj-modal-saque"
      show={show}
      onHide={onHide}
      centered
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <div>
            <div className="hpj-modal-eyebrow">Fundos PJ</div>
            <Modal.Title>Registrar Saque</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="hpj-saque-context">
            <div>
              <span>Fundo</span>
              <strong>{nomesFundos[formik.values.tipoFundo] || '-'}</strong>
            </div>
            <div>
              <span>Saldo atual</span>
              <strong>{moeda(saldoAtual)}</strong>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className={formik.touched.tipoFundo && formik.errors.tipoFundo ? 'has-error' : ''}>
                <Form.Label>
                  Fundo
                  <span className="hpj-required">*</span>
                </Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isSearchable={false}
                  options={fundosOptions}
                  placeholder="Selecione"
                  value={fundoSelecionado}
                  onBlur={() => formik.setFieldTouched('tipoFundo', true)}
                  onChange={alterarFundo}
                />
                {erroCampo('tipoFundo')}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className={formik.touched.codigoPj && formik.errors.codigoPj ? 'has-error' : ''}>
                <Form.Label>
                  Prestador PJ
                  <span className="hpj-required">*</span>
                </Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isLoading={carregandoPrestadores}
                  isSearchable
                  isDisabled={!formik.values.tipoFundo}
                  options={prestadoresComSaldo}
                  placeholder={
                    formik.values.tipoFundo
                      ? (carregandoPrestadores ? 'Carregando...' : 'Selecione')
                      : 'Selecione um fundo'
                  }
                  value={prestadorSelecionado}
                  onBlur={() => formik.setFieldTouched('codigoPj', true)}
                  onChange={alterarPrestador}
                />
                {erroCampo('codigoPj')}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className={formik.touched.valor && formik.errors.valor ? 'has-error' : ''}>
                <Form.Label>
                  Valor
                  <span className="hpj-required">*</span>
                </Form.Label>
                <Form.Control
                  name="valor"
                  inputMode="decimal"
                  placeholder="0,00"
                  type="text"
                  value={formik.values.valor}
                  onBlur={formik.handleBlur}
                  onChange={event => {
                    const value = event.target.value.replace(/[^0-9,.]/g, '');
                    formik.setFieldValue('valor', value);
                  }}
                />
                {erroCampo('valor')}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Observação</Form.Label>
                <Form.Control
                  name="observacao"
                  value={formik.values.observacao}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
                {erroCampo('observacao')}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button className="hpj-btn" variant="outline-secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button
            className="hpj-btn hpj-btn-primary"
            type="submit"
            disabled={formik.isSubmitting || carregandoPrestadores || !formik.values.tipoFundo || !formik.values.codigoPj || Number(saldoAtual || 0) <= 0}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalSaque;
